/*!
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { FC, useEffect, useState } from "react";
import { Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useFlag } from "@unleash/proxy-client-react";
import {
  isBrowserOffline,
  setNetworkAlertEnabled,
  subscribeOfflineRequestFailure,
} from "../../utils/networkError";

export const NETWORK_STATUS_ALERT_FLAG = "network-status-alert";

/**
 * Fixed-position warning shown while the browser is offline. No feature flag
 * check here; see NetworkStatusAlert.
 */
export const NetworkStatusBanner: FC = () => {
  const { t } = useTranslation("common");
  const [offline, setOffline] = useState(isBrowserOffline);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const handleOffline = () => {
      setOffline(true);
      setDismissed(false);
    };
    const handleOnline = () => setOffline(false);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    // A request failed while offline: show the warning again even if dismissed.
    const unsubscribe = subscribeOfflineRequestFailure(handleOffline);
    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
      unsubscribe();
    };
  }, []);

  if (!offline || dismissed) return null;

  return (
    <Alert
      variant="warning"
      dismissible
      onClose={() => setDismissed(true)}
      role="alert"
      className="network-status-alert mb-0 rounded-0 text-center"
      style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 2000 }}
    >
      {t("network.offline_alert")}
    </Alert>
  );
};

/**
 * App-wide offline warning. Mount once per app.
 * Behind the `network-status-alert` flag. While off, requests failing offline
 * are reported by NetworkRequestErrorAlert like any other connection error.
 */
export const NetworkStatusAlert: FC = () => {
  const enabled = useFlag(NETWORK_STATUS_ALERT_FLAG);

  useEffect(() => {
    setNetworkAlertEnabled(enabled);
  }, [enabled]);

  return enabled ? <NetworkStatusBanner /> : null;
};

export default NetworkStatusAlert;
